import { useParams, useHistory } from 'react-router-dom';
import { FC, ReactNode, createContext, useContext, useState } from 'react';

import { useGetPartnersSectionPermissions } from 'modules/partners/hooks/useGetPartnerSectionsPermissions';
import { PartnerEncumbrancesContextType } from '../types/PartnerEncumbrancesContextType';
import { partnerEncumbrancesInitValue } from '../constants/partnerEncumbrancesInitValue';
import { useGetPartnerEncumbrances } from '../hooks/useGetPartnerEncumbrances';

const PartnerEncumbrancesContext =
  createContext<PartnerEncumbrancesContextType>(partnerEncumbrancesInitValue);

export const PartnerEncumbrancesProvider: FC<{
  children: ReactNode;
  partnerId?: string;
}> = ({ children, partnerId }) => {
  const history = useHistory();
  const { societyId } = useParams();

  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const { data: permissionsData } = useGetPartnersSectionPermissions();

  const { data: partnerEncumbrancesData } = useGetPartnerEncumbrances({
    partnerId,
    page,
    rowsPerPage,
  });

  const handleToggleRowExpansion = (rowId: number) => {
    setExpandedRows((prevExpandedRows) => {
      const newExpandedRows = new Set(prevExpandedRows);
      if (newExpandedRows.has(rowId)) {
        newExpandedRows.delete(rowId);
      } else {
        newExpandedRows.add(rowId);
      }
      return newExpandedRows;
    });
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  return (
    <PartnerEncumbrancesContext.Provider
      value={{
        data: {
          encumbrances: partnerEncumbrancesData?.encumbrances || [],
          expandedRows,
          totalEncumbrances: partnerEncumbrancesData?.totalEncumbrances || 0,
          page,
          rowsPerPage,
          isAdmin: permissionsData?.isAdmin,
          isReadOnly: permissionsData?.isReadOnly,
          isLoading: false,
        },
        actions: {
          handleToggleRowExpansion,
          handleViewEncumbrance: () => {},
          handleChangePage,
          handleChangeRowsPerPage,
        },
      }}
    >
      {children}
    </PartnerEncumbrancesContext.Provider>
  );
};

export const usePartnerEncumbrances = () => {
  const context = useContext(PartnerEncumbrancesContext);
  if (!context) {
    throw new Error(
      'usePartnerEncumbrances must be used within a PartnerEncumbrancesProvider'
    );
  }
  return context;
};
